/**
 * Gantt Chart Redux Slice
 *
 * Manages the state for Gantt Chart component, including:
 * - Filter values (product lines, countries, dates, scenarios, etc.)
 * - Chart generation tracking
 * - Raw and filtered chart data
 * - Chart display settings (view mode, visibility)
 * - Selection state for batch updates
 * - Modal state for program details
 * - UI state (notifications, loading states)
 *
 * @module store/slices/ganttChartSlice
 */

import { createSlice, PayloadAction, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { logger } from 'src/utils/logger';
import { RootState } from '../store';
import {
  GanttChartFilters,
  GanttProgramData,
  GanttRPNData,
  ProgramDetailsModalData,
} from 'src/types/gantt/ganttChart.types';
import { fetchGanttChartDataAPI, saveGanttChartSettingsAPI } from 'src/services/api/ganttChartService';
import { SaveGanttChartSettingsInput } from 'src/types/gantt/ganttChart.types';

// ============================================================================
// Notification Interface
// ============================================================================

/**
 * Notification interface for user feedback messages
 */
interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  header: string;
  content: string;
  dismissible?: boolean;
  onDismiss?: () => void;
}

// ============================================================================
// Default State Values
// ============================================================================

/**
 * Default filter state with required fields initialized
 */
export const DEFAULT_FILTER_STATE: GanttChartFilters = {
  productLines: [],
  scenarios: 0,
  countries: [],
  streetDateRange: {
    startDate: '',
    endDate: '',
  },
  programRpnIds: [],
  productSubcategories: [],
  programStatus: [],
  roadmapProgram: [],
  programNameApproved: [],
  prfaqApprovalStatus: [],
  brdApproved: [],
};

// ============================================================================
// State Interface
// ============================================================================

interface GanttChartState {
  // Filter state
  filters: GanttChartFilters;
  lastGeneratedFilters: GanttChartFilters | null;
  lastMandatoryFilters: Partial<GanttChartFilters> | null;

  // Chart data
  rawGanttChartData: GanttProgramData[] | null;

  // Chart display settings
  useFirstStreetDate: boolean;
  showMissingData: boolean;

  // Modal state (for single program editing)
  selectedProgram: ProgramDetailsModalData | null;
  selectedMSRPFpnId: number | null;
  selectedHardwareDevStatus: string | null;

  // UI state - notifications
  notifications: Notification[];

  // Loading states
  isGeneratingChart: boolean;
  isSavingProgram: boolean;
  isChartLoading: boolean;
  isFirstLoad: boolean;

  // Error state
  error: string | null;

  rpnIdToAliasMap: Record<number, string>;
}

const initialState: GanttChartState = {
  // Filter state
  filters: DEFAULT_FILTER_STATE,
  lastGeneratedFilters: null,
  lastMandatoryFilters: null,

  // Chart data
  rawGanttChartData: null,

  // Chart display settings
  useFirstStreetDate: true,
  showMissingData: true,

  // Modal state
  selectedProgram: null,
  selectedMSRPFpnId: null,
  selectedHardwareDevStatus: null,

  // UI state - notifications
  notifications: [],

  // Loading states
  isGeneratingChart: true, // marking true since first time we are autoloading data
  isSavingProgram: false,
  isChartLoading: false,
  isFirstLoad: true, // Track first load to disable Generate Chart button initially

  // Error state
  error: null,

  rpnIdToAliasMap: {},
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetches Gantt chart data from the API
 */
export const fetchGanttChartData = createAsyncThunk(
  'ganttChart/fetchData',
  async (filters: GanttChartFilters, { rejectWithValue }) => {
    try {
      const data = await fetchGanttChartDataAPI(filters);
      return data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch Gantt chart data');
    }
  }
);

/**
 * Saves Gantt chart settings (batch update)
 */
export const saveGanttSettings = createAsyncThunk(
  'ganttChart/saveSettings',
  async (settings: SaveGanttChartSettingsInput[], { rejectWithValue }) => {
    try {
      const response = await saveGanttChartSettingsAPI(settings);
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to save Gantt chart settings');
    }
  }
);

// ============================================================================
// Slice
// ============================================================================

export const ganttChartSlice = createSlice({
  name: 'ganttChart',
  initialState,
  reducers: {
    // Filter management
    setFilters: (state, action: PayloadAction<GanttChartFilters>) => {
      state.filters = action.payload;
      if (state.lastGeneratedFilters !== null) {
        state.isFirstLoad = false;
      }
    },
    updateFilter: (state, action: PayloadAction<{ key: keyof GanttChartFilters; value: unknown }>) => {
      const { key, value } = action.payload;
      (state.filters as Record<string, unknown>)[key] = value;
    },
    clearFilters: (state) => {
      state.filters = DEFAULT_FILTER_STATE;
      state.lastGeneratedFilters = null;
      state.lastMandatoryFilters = null;
      state.rawGanttChartData = null;
      state.notifications = [];
      state.isFirstLoad = true;
      state.error = null;
    },
    // Individual filter setters
    setProductLines: (state, action: PayloadAction<number[]>) => {
      state.filters.productLines = action.payload;
    },
    setScenarios: (state, action: PayloadAction<number>) => {
      state.filters.scenarios = action.payload;
    },
    setCountries: (state, action: PayloadAction<string[]>) => {
      state.filters.countries = action.payload;
    },
    setStreetDateRange: (state, action: PayloadAction<{ startDate: string; endDate: string }>) => {
      state.filters.streetDateRange = action.payload;
    },
    setLastGeneratedFilters: (state, action: PayloadAction<GanttChartFilters | null>) => {
      state.lastGeneratedFilters = action.payload;
    },
    setLastMandatoryFilters: (state, action: PayloadAction<Partial<GanttChartFilters> | null>) => {
      state.lastMandatoryFilters = action.payload;
    },
    setRawGanttChartData: (state, action: PayloadAction<GanttProgramData[] | null>) => {
      state.rawGanttChartData = action.payload;
    },

    // Chart display settings
    setUseFirstStreetDate: (state, action: PayloadAction<boolean>) => {
      state.useFirstStreetDate = action.payload;
    },
    setShowMissingData: (state, action: PayloadAction<boolean>) => {
      state.showMissingData = action.payload;
    },

    // Modal state
    setSelectedProgram: (state, action: PayloadAction<ProgramDetailsModalData | null>) => {
      state.selectedProgram = action.payload;
      if (action.payload) {
        state.selectedMSRPFpnId = action.payload.rpn.selected_msrp_fpn_id || null;
        state.selectedHardwareDevStatus = action.payload.rpn.hw_development_status || null;
      }
    },
    setSelectedMSRPFpnId: (state, action: PayloadAction<number | null>) => {
      state.selectedMSRPFpnId = action.payload;
    },
    setSelectedHardwareDevStatus: (state, action: PayloadAction<string | null>) => {
      state.selectedHardwareDevStatus = action.payload;
    },
    closeModal: (state) => {
      state.selectedProgram = null;
      state.selectedMSRPFpnId = null;
      state.selectedHardwareDevStatus = null;
    },

    // UI state - notifications
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'onDismiss'>>) => {
      const id = Date.now().toString();
      const notification: Notification = {
        ...action.payload,
        id,
        dismissible: true,
        onDismiss: undefined, // Will be set by component
      };
      state.notifications.push(notification);
    },
    dismissNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },

    // Loading states
    setIsGeneratingChart: (state, action: PayloadAction<boolean>) => {
      state.isGeneratingChart = action.payload;
    },
    setIsSavingProgram: (state, action: PayloadAction<boolean>) => {
      state.isSavingProgram = action.payload;
    },
    setIsChartLoading: (state, action: PayloadAction<boolean>) => {
      state.isChartLoading = action.payload;
    },
    setIsFirstLoad: (state, action: PayloadAction<boolean>) => {
      state.isFirstLoad = action.payload;
    },

    // Error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Chart data cache
    setRpnIdToAliasMap: (state, action: PayloadAction<Record<number, string>>) => {
      state.rpnIdToAliasMap = action.payload;
    },

    // Reset state
    resetGanttChartState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch Gantt chart data
      .addCase(fetchGanttChartData.pending, (state) => {
        state.isGeneratingChart = true;
        state.isChartLoading = true;
        state.error = null;
      })
      .addCase(fetchGanttChartData.fulfilled, (state, action) => {
        state.rawGanttChartData = action.payload;
        state.isGeneratingChart = false;
        state.isChartLoading = false;
        state.isFirstLoad = false;
      })
      .addCase(fetchGanttChartData.rejected, (state, action) => {
        state.isGeneratingChart = false;
        state.isChartLoading = false;
        state.error = action.payload as string;
        // Add error notification
        const id = Date.now().toString();
        state.notifications.push({
          id,
          type: 'error',
          header: 'Failed to Load Chart Data',
          content: (action.payload as string) || 'An error occurred while loading the chart data. Please try again.',
          dismissible: true,
        });
      })
      // Save settings
      .addCase(saveGanttSettings.pending, (state) => {
        state.isSavingProgram = true;
      })
      .addCase(saveGanttSettings.fulfilled, (state) => {
        state.isSavingProgram = false;
      })
      .addCase(saveGanttSettings.rejected, (state, action) => {
        state.isSavingProgram = false;
        state.error = action.payload as string;
      });
  },
});

// ============================================================================
// Actions
// ============================================================================

export const {
  setFilters,
  updateFilter,
  clearFilters,
  setProductLines,
  setScenarios,
  setCountries,
  setStreetDateRange,
  setLastGeneratedFilters,
  setLastMandatoryFilters,
  setRawGanttChartData,
  setUseFirstStreetDate,
  setShowMissingData,
  setSelectedProgram,
  setSelectedMSRPFpnId,
  setSelectedHardwareDevStatus,
  closeModal,
  addNotification,
  dismissNotification,
  clearNotifications,
  setIsGeneratingChart,
  setIsSavingProgram,
  setIsChartLoading,
  setIsFirstLoad,
  setError,
  setRpnIdToAliasMap,
  resetGanttChartState,
} = ganttChartSlice.actions;

// ============================================================================
// Basic Selectors
// ============================================================================

export const selectFilters = (state: RootState) => state.ganttChart.filters;
export const selectLastGeneratedFilters = (state: RootState) => state.ganttChart.lastGeneratedFilters;
export const selectLastMandatoryFilters = (state: RootState) => state.ganttChart.lastMandatoryFilters;
export const selectRawGanttChartData = (state: RootState) => state.ganttChart.rawGanttChartData;
export const selectUseFirstStreetDate = (state: RootState) => state.ganttChart.useFirstStreetDate;
export const selectShowMissingData = (state: RootState) => state.ganttChart.showMissingData;
export const selectSelectedProgram = (state: RootState) => state.ganttChart.selectedProgram;
export const selectSelectedMSRPFpnId = (state: RootState) => state.ganttChart.selectedMSRPFpnId;
export const selectSelectedHardwareDevStatus = (state: RootState) => state.ganttChart.selectedHardwareDevStatus;
export const selectIsGeneratingChart = (state: RootState) => state.ganttChart.isGeneratingChart;
export const selectIsSavingProgram = (state: RootState) => state.ganttChart.isSavingProgram;
export const selectIsChartLoading = (state: RootState) => state.ganttChart.isChartLoading;
export const selectIsFirstLoad = (state: RootState) => state.ganttChart.isFirstLoad;
export const selectGanttChartError = (state: RootState) => state.ganttChart.error;
export const selectRpnIdToAliasMap = (state: RootState) => state.ganttChart.rpnIdToAliasMap;
export const selectNotifications = (state: RootState) => state.ganttChart.notifications;

// ============================================================================
// Helper Functions for Selectors
// ============================================================================

const getMandatoryFields = (filters: GanttChartFilters): Partial<GanttChartFilters> => ({
  productLines: filters.productLines,
  scenarios: filters.scenarios,
  countries: filters.countries,
  streetDateRange: filters.streetDateRange,
});

const arraysEqual = (arr1: unknown[] | undefined, arr2: unknown[] | undefined): boolean => {
  const a1 = arr1 || [];
  const a2 = arr2 || [];
  if (a1.length !== a2.length) return false;
  return JSON.stringify([...a1].sort()) === JSON.stringify([...a2].sort());
};

// ============================================================================
// Filter Helper Functions
// ============================================================================

/**
 * Filter configuration type for defining filter rules
 */
type FilterConfig<T> = {
  filterValues: T[] | undefined;
  getValue: (rpn: GanttRPNData) => T | null | undefined;
};

/**
 * Generalized filter function that applies array-based filters to RPN data
 * This function handles all filter types in a consistent, testable manner
 *
 * IMPORTANT: Programs with null/undefined values are INCLUDED in results to ensure
 * they appear in the missing data table. We only filter out programs that have
 * actual values that don't match the filter criteria.
 *
 * @param {GanttRPNData[]} rpns - Array of RPN objects to filter
 * @param {FilterConfig<T>} config - Filter configuration containing values and getter function
 * @returns {GanttRPNData[]} Filtered RPN array
 *
 * @example
 * // Filter by program status
 * const filtered = applyArrayFilter(rpns, {
 *   filterValues: ['Active', 'Pending'],
 *   getValue: (rpn) => rpn.program_status
 * });
 */
const applyArrayFilter = <T>(rpns: GanttRPNData[], config: FilterConfig<T>): GanttRPNData[] => {
  const { filterValues, getValue } = config;

  // Skip filtering if no filter values provided
  if (!filterValues || filterValues.length === 0) {
    return rpns;
  }

  return rpns.filter((rpn) => {
    const value = getValue(rpn);
    // INCLUDE programs with null/undefined values so they appear in missing data table
    // ONLY filter out programs that have a value that doesn't match the filter
    if (value === null || value === undefined) {
      return true; // Include in results
    }
    return filterValues.includes(value);
  });
};

/**
 * Applies all configured filters to an RPN array sequentially
 * Each filter is applied independently and can be easily tested
 *
 * @param {GanttRPNData[]} rpns - Array of RPN objects to filter
 * @param {GanttChartFilters} filters - Filter configuration object
 * @returns {GanttRPNData[]} Filtered RPN array
 */
const applyAllFilters = (rpns: GanttRPNData[], filters: GanttChartFilters): GanttRPNData[] => {
  let result: GanttRPNData[] = [...rpns];

  // Define all filter configurations in a declarative way
  const filterConfigs: Array<FilterConfig<number | string | boolean>> = [
    {
      filterValues: filters.programRpnIds,
      getValue: (rpn: GanttRPNData) => rpn.rpn_id,
    },
    {
      filterValues: filters.productSubcategories,
      getValue: (rpn: GanttRPNData) => rpn.product_line_metadata?.product_subcategory,
    },
    {
      filterValues: filters.programStatus,
      getValue: (rpn: GanttRPNData) => rpn.program_status,
    },
    {
      filterValues: filters.roadmapProgram,
      getValue: (rpn: GanttRPNData) => rpn.is_on_roadmap,
    },
    {
      filterValues: filters.programNameApproved,
      getValue: (rpn: GanttRPNData) => rpn.is_name_approved,
    },
    {
      filterValues: filters.prfaqApprovalStatus,
      getValue: (rpn: GanttRPNData) => rpn.prfaq_approval_status,
    },
    {
      filterValues: filters.brdApproved,
      getValue: (rpn: GanttRPNData) => rpn.is_brd_approved,
    },
  ];

  // Apply each filter sequentially
  filterConfigs.forEach((config) => {
    result = applyArrayFilter(result, config);
  });

  return result;
};

// ============================================================================
// Memoized Selectors
// ============================================================================

/**
 * Checks if all mandatory filters have valid values
 */
export const selectMandatoryFiltersValid = createSelector([selectFilters], (filters) => {
  const hasRequiredFields =
    filters.productLines.length > 0 &&
    filters.scenarios > 0 &&
    filters.countries.length > 0 &&
    filters.streetDateRange.startDate.trim() !== '' &&
    filters.streetDateRange.endDate.trim() !== '';

  if (!hasRequiredFields) {
    return false;
  }

  // Validate date range: end date must be greater than start date
  const startDate = new Date(filters.streetDateRange.startDate);
  const endDate = new Date(filters.streetDateRange.endDate);

  return endDate > startDate;
});

/**
 * Checks if any filters have changed since last chart generation
 */
export const selectHaveFiltersChanged = createSelector(
  [selectFilters, selectLastGeneratedFilters],
  (filters, lastGeneratedFilters) => {
    if (!lastGeneratedFilters) return true;

    return !(
      arraysEqual(filters.productLines, lastGeneratedFilters.productLines) &&
      filters.scenarios === lastGeneratedFilters.scenarios &&
      arraysEqual(filters.countries, lastGeneratedFilters.countries) &&
      filters.streetDateRange.startDate === lastGeneratedFilters.streetDateRange.startDate &&
      filters.streetDateRange.endDate === lastGeneratedFilters.streetDateRange.endDate &&
      arraysEqual(filters.programRpnIds, lastGeneratedFilters.programRpnIds) &&
      arraysEqual(filters.productSubcategories, lastGeneratedFilters.productSubcategories) &&
      arraysEqual(filters.programStatus, lastGeneratedFilters.programStatus) &&
      arraysEqual(filters.roadmapProgram, lastGeneratedFilters.roadmapProgram) &&
      arraysEqual(filters.programNameApproved, lastGeneratedFilters.programNameApproved) &&
      arraysEqual(filters.prfaqApprovalStatus, lastGeneratedFilters.prfaqApprovalStatus) &&
      arraysEqual(filters.brdApproved, lastGeneratedFilters.brdApproved)
    );
  }
);

/**
 * Checks if mandatory filters have changed (determines if API call is needed)
 */
export const selectHaveMandatoryFieldsChanged = createSelector(
  [selectFilters, selectLastMandatoryFilters],
  (filters, lastMandatoryFilters) => {
    if (!lastMandatoryFilters) return true;

    const currentMandatory = getMandatoryFields(filters);

    const productLines1 = [...(lastMandatoryFilters.productLines || [])].sort();
    const productLines2 = [...(currentMandatory.productLines || [])].sort();
    const countries1 = [...(lastMandatoryFilters.countries || [])].sort();
    const countries2 = [...(currentMandatory.countries || [])].sort();

    return (
      JSON.stringify(productLines1) !== JSON.stringify(productLines2) ||
      lastMandatoryFilters.scenarios !== currentMandatory.scenarios ||
      JSON.stringify(countries1) !== JSON.stringify(countries2) ||
      lastMandatoryFilters.streetDateRange?.startDate !== currentMandatory.streetDateRange?.startDate ||
      lastMandatoryFilters.streetDateRange?.endDate !== currentMandatory.streetDateRange?.endDate
    );
  }
);

/**
 * Selector to check if user has access to any product lines
 * This is used to prevent chart generation when user has no access
 *
 * @returns {boolean} True if user has at least one accessible product line
 */
export const selectHasProductLineAccess = (state: RootState) => {
  const filters = selectFilters(state);
  return filters.productLines.length > 0;
};

/**
 * Memoized selector to determine if chart generation button should be enabled
 * Disabled during first load, then follows normal validation rules
 * Also checks if user has access to any product lines
 *
 * @returns {boolean} True if chart can be generated
 */
export const selectCanGenerateChart = createSelector(
  [selectMandatoryFiltersValid, selectHaveFiltersChanged, selectIsFirstLoad, selectHasProductLineAccess],
  (mandatoryValid, filtersChanged, isFirstLoad, hasProductLineAccess) => {
    // Disable button during first load
    if (isFirstLoad) {
      return false;
    }

    // Disable if user has no access to any product lines
    if (!hasProductLineAccess) {
      return false;
    }

    // After first load, enable if mandatory filters are valid and filters have changed
    return mandatoryValid && filtersChanged;
  }
);

/**
 * Memoized selector that applies optional filters to raw chart data
 * Filters data client-side based on non-mandatory filter selections
 *
 * @returns {GanttProgramData[] | null} Filtered chart data or null if no data available
 */
export const selectFilteredGanttChartData = createSelector(
  [selectRawGanttChartData, selectLastGeneratedFilters],
  (rawData, lastGeneratedFilters): GanttProgramData[] | null => {
    // Add comprehensive defensive checks
    if (!rawData || !lastGeneratedFilters) {
      return null;
    }

    // Ensure rawData is an array
    if (!Array.isArray(rawData)) {
      logger.warn(`[selectFilteredGanttChartData] rawData is not an array: ${typeof rawData}`);
      return null;
    }

    // Ensure rawData has items
    if (rawData.length === 0) {
      return [];
    }

    // Apply non-mandatory filters to raw data
    try {
      return rawData.map((productLine): GanttProgramData => {
        // Defensive check for productLine
        if (!productLine || typeof productLine !== 'object') {
          logger.warn(`[selectFilteredGanttChartData] Invalid productLine: ${JSON.stringify(productLine)}`);
          return productLine as GanttProgramData;
        }

        if (!productLine.rpns || !Array.isArray(productLine.rpns)) {
          return productLine;
        }

        // Apply all filters using the generalized filter function
        const filteredRpns = applyAllFilters(productLine.rpns, lastGeneratedFilters);

        return {
          ...productLine,
          rpns: filteredRpns,
        };
      });
    } catch (error) {
      logger.error(
        `[selectFilteredGanttChartData] Error filtering data: ${error instanceof Error ? error.message : String(error)}`
      );
      // Return raw data as fallback
      return rawData as GanttProgramData[];
    }
  }
);

export default ganttChartSlice.reducer;
